import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { isSSOEnabled, performLogout, clearServiceWorkers } from '@loomstateorg/shared-frontend/utils/sso';

// Type guard to check if session has idToken
function hasIdToken(session: any): session is { idToken: string } {
  return session && typeof session === 'object' && 'idToken' in session && typeof session.idToken === 'string';
}

/**
 * Custom hook for SSO functionality
 * Provides common SSO operations and state
 */
export const useSSO = () => {
  const sessionResult = useSession();
  const router = useRouter();
  
  // Handle case where useSession returns undefined (shouldn't happen with proper SessionProvider)
  const { data: session, status } = sessionResult || { data: null, status: 'loading' as const };
  
  // Get SSO enabled status - this should work in both client and server environments
  let ssoEnabled = false;
  try {
    ssoEnabled = isSSOEnabled();
  } catch (error) {
    // Fallback if environment variables are not available
    ssoEnabled = false;
  }

  /**
   * Perform logout with service worker cleanup
   */
  const logout = useCallback(async (commercetoolsLogout: () => Promise<void>) => {
    // Clear service workers first
    await clearServiceWorkers();
    
    // Perform the actual logout
    await performLogout(session, commercetoolsLogout, router);
  }, [session, router]);

  /**
   * Check if current user is an SSO user
   */
  const isSSO = useCallback(() => {
    return hasIdToken(session);
  }, [session]);

  /**
   * Check if user is authenticated (either SSO or traditional)
   */
  const isAuthenticated = useCallback(() => {
    return status === 'authenticated';
  }, [status]);

  /**
   * Check if authentication is still loading
   */
  const isLoading = useCallback(() => {
    return status === 'loading';
  }, [status]);

  return {
    session,
    status,
    ssoEnabled,
    isSSO: isSSO(),
    isAuthenticated: isAuthenticated(),
    isLoading: isLoading(),
    logout,
  };
};